import { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";

import Tooltip from "./Tooltip";
import { INote } from "../../models/Note";
import { IPart } from "../../models/Part";
import ContactFromId from "../contacts/ContactFromId";
import formatDateTime from "../../utils/formatDateTime";


/**
 * Warning icon for a part with a damage report against it, used on every part table.
 *
 * The report itself is a single field that the "Mark Damaged" and "Mark Missing" flows
 * both overwrite, so it is always the latest one - isDamaged/isMissing say which flow
 * wrote it. The newest part note is shown underneath it as context, since that is where
 * anything said after the report lands.
 */
export default function DamageNoteIndicator({ part }: { part: IPart }) {
  const { t } = useTranslation("common");

  const message = part.damageMessage?.trim() || null;

  // Listing rows carry only the newest note (latestNote); the stock table returns the
  // full arrays instead, so fall back to those rather than showing nothing there.
  const note = useMemo<INote | null>(() => {
    if (part.latestNote) return part.latestNote;

    const all = [...(part.notes ?? []), ...(part.internalNotes ?? [])];
    if (all.length === 0) return null;

    const when = (n: INote) => n.date ?? n.created;
    return all.reduce((newest, n) => (when(n) > when(newest) ? n : newest));
  }, [part.latestNote, part.notes, part.internalNotes]);

  // Nothing to report: no reason typed in and the part was never flagged.
  if (!message && !part.isDamaged) return null;

  const heading = part.isDamaged
    ? t("Damaged")
    : part.isMissing
      ? t("Missing")
      : part.isNotAccessible
        ? t("Not Accessible")
        : t("Damage Reported");

  const reportedOn = formatDateTime(part.damageReportedOn, DateTime.DATETIME_SHORT);

  return (
    <Tooltip
      content={
        <div className="max-w-xs whitespace-pre-wrap text-left">
          <p className="font-semibold">
            {heading}
            {reportedOn && <span className="font-normal"> &mdash; {reportedOn}</span>}
            {part.damageReportedBy && (
              <span className="font-normal">
                {", "}
                <ContactFromId contactId={part.damageReportedBy} />
              </span>
            )}
          </p>
          <p className="mt-1">{message ?? t("No reason given")}</p>
          {note && (
            <div className="mt-2 border-t border-gray-700 pt-2">
              <p className="text-xs text-gray-300">
                {t("Latest note")}
                {" — "}
                {formatDateTime(note.date ?? note.created, DateTime.DATETIME_SHORT)}
                {note.createdBy && (
                  <>
                    {", "}
                    <ContactFromId contactId={note.createdBy} />
                  </>
                )}
              </p>
              <p className="mt-1">{note.message}</p>
            </div>
          )}
        </div>
      }
    >
      <ExclamationTriangleIcon
        className="h-5 w-5 shrink-0 text-red-500 hover:scale-125 cursor-help transition duration-200"
        onClick={(e) => e.stopPropagation()}
      />
    </Tooltip>
  );
}
